import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Megaphone, Paperclip, Trash2, Loader2, Upload } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Updates = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    attachment_url: ''
  });

  useEffect(() => {
    fetchUpdates();
  }, []);

  const fetchUpdates = async () => {
    try {
      const response = await axios.get(`${API}/updates`);
      setUpdates(response.data);
    } catch (error) {
      console.error('Failed to fetch updates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, attachment_url: response.data.url }));
      toast.success('Datei hochgeladen');
    } catch (error) {
      toast.error('Fehler beim Hochladen');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Bitte Titel und Inhalt eingeben');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/updates`, formData);
      setUpdates([response.data, ...updates]);
      setFormData({ title: '', content: '', attachment_url: '' });
      setDialogOpen(false);
      toast.success(t('updatePosted'));
    } catch (error) {
      toast.error('Fehler beim Veröffentlichen');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (updateId) => {
    try {
      await axios.delete(`${API}/updates/${updateId}`);
      setUpdates(updates.filter(u => u.id !== updateId));
      toast.success('Mitteilung gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="updates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 font-[Manrope]">{t('updates')}</h2>
          <p className="text-slate-500 mt-1 text-sm">Unternehmensmitteilungen</p>
        </div>
        
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-[#D92632] hover:bg-[#B91C26] text-white h-9 sm:h-10 text-sm"
                data-testid="post-update-btn"
              >
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('postUpdate')}</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto mx-4">
              <DialogHeader>
                <DialogTitle className="font-[Manrope]">{t('postUpdate')}</DialogTitle>
                <DialogDescription>Veröffentlichen Sie eine Unternehmensmitteilung</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>{t('updateTitle')} *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="z.B. Wichtige Ankündigung"
                    className="mt-1"
                    data-testid="update-title-input"
                  />
                </div>
                
                <div>
                  <Label>{t('updateContent')} *</Label>
                  <Textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Inhalt der Mitteilung..."
                    className="mt-1"
                    rows={6}
                    data-testid="update-content-input"
                  />
                </div>
                
                <div>
                  <Label>{t('attachment')}</Label>
                  <div className="mt-1">
                    <input
                      type="file"
                      id="attachment"
                      className="hidden"
                      onChange={handleFileUpload}
                    />
                    <label htmlFor="attachment">
                      <Button
                        type="button"
                        variant="outline"
                        className="cursor-pointer"
                        disabled={uploading}
                        asChild
                      >
                        <span>
                          {uploading ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4 mr-2" />
                          )}
                          Datei auswählen
                        </span>
                      </Button>
                    </label>
                    {formData.attachment_url && (
                      <span className="ml-2 text-sm text-slate-500">
                        Datei angehängt ✓
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>
                    {t('cancel')}
                  </Button>
                  <Button 
                    onClick={handleSubmit}
                    className="bg-[#D92632] hover:bg-[#B91C26]"
                    disabled={saving}
                    data-testid="publish-update-btn"
                  >
                    {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Veröffentlichen
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Updates List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : updates.length === 0 ? (
        <Card className="bg-white border border-slate-200">
          <CardContent className="py-12 text-center text-slate-500">
            {t('noData')}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {updates.map((update) => (
            <Card 
              key={update.id} 
              className="bg-white border border-slate-200 shadow-sm"
              data-testid={`update-${update.id}`}
            >
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#D92632]/10 flex items-center justify-center flex-shrink-0">
                    <Megaphone className="h-5 w-5 sm:h-6 sm:w-6 text-[#D92632]" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-base sm:text-lg font-semibold text-slate-900 font-[Manrope]">
                          {update.title}
                        </h3>
                        <p className="text-xs sm:text-sm text-slate-500 mt-1">
                          {update.created_by_name} • {format(new Date(update.created_at), 'PPP', { locale: de })}
                        </p>
                      </div>
                      
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-slate-400 hover:text-red-500"
                          onClick={() => handleDelete(update.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    
                    <p className="mt-4 text-slate-700 whitespace-pre-wrap">
                      {update.content}
                    </p>
                    
                    {update.attachment_url && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${update.attachment_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 mt-4 px-3 py-2 bg-slate-100 rounded-lg text-sm text-slate-700 hover:bg-slate-200 transition-colors"
                      >
                        <Paperclip className="h-4 w-4" />
                        Anhang öffnen
                      </a>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Updates;
